import { add, sub } from 'date-fns';
import React from 'react';

export type TMonthDate = {
  date: Date;
  isSameMonth: boolean;
  isSameDay: boolean;
  canSelect: boolean;
  isWeekend: boolean;
};

export type TWeekInfo = {
  weekStart: Date;
  weekEnd: Date;
  days: {
    date: Date;
    isSameMonth: boolean;
    isSameDay: boolean;
    isWeekend: boolean;
    canSelect: boolean;
  }[];
};

export type TMonthMatrix = {
  start: Date;
  end: Date;
  maxDate?: Date;
  minDate?: Date;
  weeks: TWeekInfo[];
};

export function skipIntervals(
  date: Date = new Date(),
  intervals: number,
  direction: 'add' | 'sub',
  duration: 'days' | 'weeks' | 'months' | 'years' = 'days',
) {
  return direction === 'add'
    ? add(date, {
        [duration]: intervals,
      })
    : sub(date, {
        [duration]: intervals,
      });
}

/**
 * Run callback every full interval
 * Inspired by https://stackoverflow.com/questions/12309019/javascript-how-to-do-something-every-full-hour/12309126
 * @param intervalInMs time in ms
 * @param callback callback function to run
 * @param internalHandlerCallback callback after initial timeout is executed, to get reference for real interval function
 * @returns returns timeout handler
 */
export function runOnInterval(
  intervalInMs: number,
  callback: Function,
  internalHandlerCallback: (handler: any) => void,
) {
  return setTimeout(() => {
    callback();
    internalHandlerCallback(setInterval(callback, intervalInMs));
  }, intervalInMs - ((Date.now() - new Date().getTimezoneOffset() * 6e4) % intervalInMs));
}

/**
 * Hook to use interval
 * @param intervalInMs interval in ms
 * @param callback callback function to call on interval reached
 * @returns cancel function
 */
export function useOnInterval(intervalInMs: number, callback: (cancel: Function) => void) {
  const timeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const interval = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const cancel = React.useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  }, []);

  React.useEffect(() => {
    timeout.current = runOnInterval(intervalInMs, callback, (i) => {
      interval.current = i;
    });
    callback(cancel);

    return () => {
      cancel();
    };
  }, [intervalInMs, callback, cancel]);

  return cancel;
}
